import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { purchasingService } from "../services/dataService";
import { PurchaseOrderListItem, PurchaseOrderStatus, PurchasingStats, Supplier } from "../types";
import { formatCurrency, turkishIncludes } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import PageLoader from "../components/PageLoader";
import SupplierModal from "../components/SupplierModal";
import "./Receivables.css";
import "./Purchasing.css";

export const PO_STATUS_LABEL: Record<PurchaseOrderStatus, string> = {
  [PurchaseOrderStatus.Draft]: "Taslak",
  [PurchaseOrderStatus.Ordered]: "İletildi",
  [PurchaseOrderStatus.PartiallyReceived]: "Kısmen Alındı",
  [PurchaseOrderStatus.Received]: "Tamamlandı",
  [PurchaseOrderStatus.Cancelled]: "İptal",
};
const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "—");
const fmtQty = (n: number) => n.toLocaleString("tr-TR", { maximumFractionDigits: 3 });

type Tab = "orders" | "suppliers";

const Purchasing: React.FC = () => {
  const navigate = useNavigate();
  const { has } = usePermissions();
  const canSuppliers = has("purchasing:suppliers:view");
  const [tab, setTab] = useState<Tab>("orders");
  const [stats, setStats] = useState<PurchasingStats | null>(null);
  const [orders, setOrders] = useState<PurchaseOrderListItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "open" | PurchaseOrderStatus>("open");
  const [supplierModal, setSupplierModal] = useState<{ open: boolean; supplier?: Supplier | null }>({ open: false });
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [s, o] = await Promise.all([purchasingService.stats(), purchasingService.orders()]);
      setStats(s); setOrders(o);
      if (canSuppliers) setSuppliers(await purchasingService.suppliers(true));
    } catch (e: any) { setError(e?.response?.data?.message || "Satın alma verileri yüklenemedi."); }
    finally { setLoading(false); }
  }, [canSuppliers]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => orders.filter((o) => {
    if (status === "open" && !(o.status === PurchaseOrderStatus.Ordered || o.status === PurchaseOrderStatus.PartiallyReceived || o.status === PurchaseOrderStatus.Draft)) return false;
    if (status !== "" && status !== "open" && o.status !== status) return false;
    if (search.trim() && !(turkishIncludes(o.number, search) || turkishIncludes(o.supplierName, search))) return false;
    return true;
  }), [orders, status, search]);

  const supplierRows = useMemo(() => suppliers.filter((s) =>
    (showInactive || s.isActive) && (!search.trim() || turkishIncludes(s.name, search) || turkishIncludes(s.code || "", search))), [suppliers, showInactive, search]);

  const removeSupplier = async (s: Supplier) => {
    if (!window.confirm(`${s.name} silinsin mi? Siparişi varsa pasife alınır.`)) return;
    try { const r = await purchasingService.deleteSupplier(s.supplierId); window.alert(r.message); await load(); }
    catch (e: any) { setError(e?.response?.data || "Silinemedi."); }
  };

  if (loading && !stats) return <PageLoader message="Satın alma yükleniyor..." />;

  const money = (m?: Record<string, number>) => Object.entries(m || {}).map(([c, v]) => formatCurrency(v, c));

  return (
    <div className="rc-page">
      <div className="rc-head">
        <div>
          <h1>Satın Alma</h1>
          <p>Tedarikçi siparişleri, mal kabul ve alış fiyatı geçmişi. Mal kabul stoğa otomatik giriş yapar.</p>
        </div>
        <div className="rc-head-actions">
          {tab === "orders" && has("purchasing:orders:create") && (
            <button className="rc-btn primary" onClick={() => navigate("/purchasing/new")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
              Yeni Sipariş
            </button>
          )}
          {tab === "suppliers" && has("purchasing:suppliers:create") && (
            <button className="rc-btn primary" onClick={() => setSupplierModal({ open: true })}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
              Yeni Tedarikçi
            </button>
          )}
          <button className="rc-btn" onClick={load} disabled={loading}>Yenile</button>
        </div>
      </div>

      {error && <div className="ic-alert error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {stats && (
        <div className="rc-kpis">
          <div className="rc-kpi"><div className="rc-kpi-label">Açık Sipariş</div><div className="rc-kpi-value">{stats.openCount}</div><div className="rc-kpi-sub">{stats.draftCount} taslak</div></div>
          <div className={`rc-kpi ${stats.overdueCount > 0 ? "overdue" : ""}`}><div className="rc-kpi-label">Teslimi Geciken</div><div className="rc-kpi-value">{stats.overdueCount}</div><div className="rc-kpi-sub">Beklenen tarihi geçmiş</div></div>
          <div className="rc-kpi"><div className="rc-kpi-label">Bekleyen Tutar</div><div className="rc-kpi-value rc-kpi-multi">{money(stats.openAmountByCurrency).map((s) => <span key={s}>{s}</span>)}{Object.keys(stats.openAmountByCurrency).length === 0 && "—"}</div><div className="rc-kpi-sub">Teslim alınmamış kalan (KDV hariç)</div></div>
          <div className="rc-kpi collected"><div className="rc-kpi-label">Son 30 Gün Alım</div><div className="rc-kpi-value rc-kpi-multi">{money(stats.receivedLast30DaysByCurrency).map((s) => <span key={s}>{s}</span>)}{Object.keys(stats.receivedLast30DaysByCurrency).length === 0 && "—"}</div><div className="rc-kpi-sub">Mal kabul edilen (KDV hariç)</div></div>
        </div>
      )}

      <div className="pu-tabs">
        <button className={`pu-tab ${tab === "orders" ? "active" : ""}`} onClick={() => setTab("orders")}>Siparişler</button>
        {canSuppliers && <button className={`pu-tab ${tab === "suppliers" ? "active" : ""}`} onClick={() => setTab("suppliers")}>Tedarikçiler</button>}
      </div>

      <div className="rc-toolbar">
        <input className="rc-input rc-search" placeholder={tab === "orders" ? "Sipariş no veya tedarikçi ara..." : "Tedarikçi ara..."} value={search} onChange={(e) => setSearch(e.target.value)} />
        {tab === "orders" ? (
          <select className="rc-input" value={status} onChange={(e) => setStatus(e.target.value === "" || e.target.value === "open" ? (e.target.value as "" | "open") : (parseInt(e.target.value, 10) as PurchaseOrderStatus))}>
            <option value="open">Açık siparişler</option>
            <option value="">Tümü</option>
            {Object.entries(PO_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        ) : (
          <label className="rc-check"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Pasifleri göster</label>
        )}
      </div>

      {tab === "orders" ? (
        <div className="rc-card"><div className="rc-table-wrap">
          <table className="rc-table rt rt-stack">
            <thead><tr>
              <th>Sipariş</th><th>Tedarikçi</th><th className="rt-p3">Tarih</th><th className="rt-p3">Beklenen</th>
              <th>Durum</th><th className="rt-p2">Teslimat</th><th className="num">Tutar</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7}><div className="rc-empty"><strong>{orders.length ? "Filtreye uyan sipariş yok" : "Henüz satın alma siparişi yok"}</strong>{!orders.length && "İlk siparişi \"Yeni Sipariş\" ile oluşturun."}</div></td></tr>
              ) : rows.map((o) => {
                const pct = o.orderedQuantity > 0 ? Math.min(100, (o.receivedQuantity / o.orderedQuantity) * 100) : 0;
                return (
                  <tr key={o.purchaseOrderId} className="clickable" onClick={() => navigate(`/purchasing/${o.purchaseOrderId}`)}>
                    <td className="rt-main"><span className="strong">{o.number}</span><div className="muted" style={{ fontSize: "0.75rem" }}>{o.lineCount} kalem</div></td>
                    <td data-label="Tedarikçi">{o.supplierName}</td>
                    <td className="rt-p3" data-label="Tarih">{fmtDate(o.orderDate)}</td>
                    <td className="rt-p3" data-label="Beklenen">{o.isOverdue ? <span className="rc-chip danger">{fmtDate(o.expectedDate)}</span> : fmtDate(o.expectedDate)}</td>
                    <td data-label="Durum"><span className={`pu-status s${o.status}`}>{PO_STATUS_LABEL[o.status]}</span></td>
                    <td className="rt-p2" data-label="Teslimat">
                      {o.status === PurchaseOrderStatus.Draft || o.status === PurchaseOrderStatus.Cancelled ? <span className="muted">—</span> : (
                        <div className={`pu-progress ${pct >= 100 ? "done" : ""}`}><div className="pu-progress-bar"><span style={{ width: `${pct}%` }} /></div><span className="pu-progress-text">{fmtQty(o.receivedQuantity)} / {fmtQty(o.orderedQuantity)}</span></div>
                      )}
                    </td>
                    <td className="num strong" data-label="Tutar">{formatCurrency(o.grossTotal, o.currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div></div>
      ) : (
        <div className="rc-card"><div className="rc-table-wrap">
          <table className="rc-table rt rt-stack">
            <thead><tr>
              <th>Tedarikçi</th><th className="rt-p3">İlgili</th><th className="rt-p3">Telefon / E-posta</th><th className="rt-p4">VKN</th><th>Vade</th><th>Durum</th><th className="rt-actions" />
            </tr></thead>
            <tbody>
              {supplierRows.length === 0 ? (
                <tr><td colSpan={7}><div className="rc-empty"><strong>Tedarikçi yok</strong>"Yeni Tedarikçi" ile ekleyin.</div></td></tr>
              ) : supplierRows.map((s) => (
                <tr key={s.supplierId}>
                  <td className="rt-main"><span className="strong">{s.name}</span>{s.code && <div className="muted" style={{ fontSize: "0.75rem" }}>{s.code}</div>}</td>
                  <td className="rt-p3" data-label="İlgili">{s.contactPerson || "—"}</td>
                  <td className="rt-p3" data-label="İletişim">{[s.phone, s.email].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="rt-p4" data-label="VKN">{s.taxNumber || "—"}</td>
                  <td data-label="Vade">{s.paymentTermDays} gün · {s.currency}</td>
                  <td data-label="Durum">{s.isActive ? <span className="rc-chip ok">Aktif</span> : <span className="rc-chip neutral">Pasif</span>}</td>
                  <td className="rt-actions">
                    <div style={{ display: "flex", gap: "0.375rem", justifyContent: "flex-end" }}>
                      {has("purchasing:suppliers:update") && <button className="rc-btn sm" onClick={() => setSupplierModal({ open: true, supplier: s })}>Düzenle</button>}
                      {has("purchasing:suppliers:delete") && <button className="rc-btn sm danger-ghost" onClick={() => removeSupplier(s)}>Sil</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}

      {supplierModal.open && (
        <SupplierModal supplier={supplierModal.supplier} onClose={() => setSupplierModal({ open: false })} onSaved={load} />
      )}
    </div>
  );
};

export default Purchasing;
